import BiReportsIcon from "@assets/images/report-bi.svg";
import FinanceReportsIcon from "@assets/images/report-finance.svg";
import { Container } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Grid from "@saleor/components/Grid";
import PageHeader from "@saleor/components/PageHeader";
import RequirePermissions from "@saleor/components/RequirePermissions";
import { User } from "@saleor/fragments/types/User";
import useNavigator from "@saleor/hooks/useNavigator";
import { PermissionEnum } from "@saleor/types/globalTypes";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import React from "react";
import SVG from "react-inlinesvg";
import { useIntl } from "react-intl";

import { BrandVerticalsListPath } from "../../../brandVerticalsOmi/url";
import { CustomerActivityListPath } from "../../../customerActivity/urls";
import { CustomerInfoListPath } from "../../../customerInfo/urls";
import { GrabExpressPath } from "../../../grabExpress/url";
import { MallOrdersListPath } from "../../../mallOrders/url";
import { OrderLinesListPath } from "../../../orderLines/urls";
import { OrderLinesFinanceListPath } from "../../../orderLinesFinance/urls";
import { ProductCategoriesReportListPath } from "../../../productCategoriesBi/url";
import { ProductDetailsBiReportListPath } from "../../../productDetailsBi/url";
import { ProductsReportsListPath } from "../../../products/urls";
import { ProductStocksListPath } from "../../../productStocks/url";
import { StoreDetailsReportListPath } from "../../../storeDetailsFinance/url";
import { StoreOrdersBiListPath } from "../../../storeOrdersBi/urls";
import { StoreOrdersFinanceListPath } from "../../../storeOrdersFinance/urls";
import { StoresReportListPath } from "../../../stores/url";
import { TransportReportListPath } from "../../../transportReport/urls";
import { VoucherListPath } from "../../../voucher/urls";
import {
  generateReportUrl,
  GenerateReportUrlDialog,
  GenerateReportUrlQueryParams
} from "../../urls";
import GenerateReportDialog from "../GenerateReportDialog/GenerateReportDialog";

const useStyles = makeStyles(
  theme => ({
    cardButton: {
      display: "block",
      textAlign: "left"
    },
    cardContent: {
      display: "grid",
      gridColumnGap: theme.spacing(4),
      gridTemplateColumns: "48px 1fr"
    },
    cardDescription: {
      marginBottom: 20
    },
    cardTitle: {
      fontSize: 20,
      fontWeight: 600 as 600
    }
  }),
  { name: "Reports" }
);

interface GenerateReportProps {
  params: GenerateReportUrlQueryParams;
  user: User;
}

const ReportsPage: React.FC<GenerateReportProps> = ({ params, user }) => {
  const intl = useIntl();
  const navigate = useNavigator();
  const classes = useStyles({});

  const [closeModal] = createDialogActionHandlers<
    GenerateReportUrlDialog,
    GenerateReportUrlQueryParams
  >(navigate, params => generateReportUrl(params), params);

  const reportCategories = [
    {
      description: "Generate data for OMI group.",
      icon: BiReportsIcon,
      permissions: [PermissionEnum.MANAGE_REPORTS],
      reportTypes: [
        {
          path: StoreOrdersBiListPath,
          permissions: [
            PermissionEnum.MANAGE_REPORTS,
            PermissionEnum.MANAGE_STORE_ORDERS
          ],
          type: "Store Orders"
        },
        {
          path: ProductStocksListPath,
          permissions: [
            PermissionEnum.MANAGE_REPORTS,
            PermissionEnum.MANAGE_STORE_ORDERS
          ],
          type: "Product Stocks"
        },
        {
          path: CustomerInfoListPath,
          permissions: [
            PermissionEnum.MANAGE_REPORTS,
            PermissionEnum.MANAGE_TENANTS
          ],
          type: "Customer Information"
        },
        {
          path: StoresReportListPath,
          permissions: [
            PermissionEnum.MANAGE_REPORTS,
            PermissionEnum.MANAGE_TENANTS
          ],
          type: "Store Details"
        },
        {
          path: BrandVerticalsListPath,
          permissions: [
            PermissionEnum.MANAGE_REPORTS,
            PermissionEnum.MANAGE_TENANTS
          ],
          type: "Brand Verticals"
        },
        {
          path: GrabExpressPath,
          permissions: [
            PermissionEnum.MANAGE_REPORTS,
            PermissionEnum.MANAGE_STORE_ORDERS
          ],
          type: "Grab Express"
        }
      ],
      title: "OMI General"
    },
    {
      description: "Generate payouts and reports related to finance.",
      icon: FinanceReportsIcon,
      permissions: [
        PermissionEnum.MANAGE_REPORTS,
        PermissionEnum.MANAGE_FINANCE
      ],
      reportTypes: [
        {
          path: StoreOrdersFinanceListPath,
          permissions: [
            PermissionEnum.MANAGE_REPORTS,
            PermissionEnum.MANAGE_FINANCE
          ],
          type: "Store Orders"
        },
        {
          path: OrderLinesFinanceListPath,
          permissions: [
            PermissionEnum.MANAGE_REPORTS,
            PermissionEnum.MANAGE_FINANCE
          ],
          type: "Order Lines"
        },
        {
          path: StoreDetailsReportListPath,
          permissions: [
            PermissionEnum.MANAGE_REPORTS,
            PermissionEnum.MANAGE_FINANCE
          ],
          type: "Store Details"
        }
      ],
      title: "Finance"
    },
    {
      description: "Generate reports related to business intelligence.",
      icon: BiReportsIcon,
      permissions: [
        PermissionEnum.MANAGE_REPORTS,
        PermissionEnum.MANAGE_TENANTS
      ],
      reportTypes: [
        {
          path: CustomerActivityListPath,
          permissions: [
            PermissionEnum.MANAGE_REPORTS,
            PermissionEnum.MANAGE_TENANTS
          ],
          type: "Customer Activities"
        },
        {
          path: ProductDetailsBiReportListPath,
          permissions: [
            PermissionEnum.MANAGE_REPORTS,
            PermissionEnum.MANAGE_TENANTS
          ],
          type: "Product Details"
        },
        {
          path: ProductCategoriesReportListPath,
          permissions: [
            PermissionEnum.MANAGE_REPORTS,
            PermissionEnum.MANAGE_TENANTS
          ],
          type: "Product Categories"
        },
        {
          path: VoucherListPath,
          permissions: [
            PermissionEnum.MANAGE_REPORTS,
            PermissionEnum.MANAGE_TENANTS
          ],
          type: "Voucher Report"
        },
        {
          path: ProductsReportsListPath,
          permissions: [
            PermissionEnum.MANAGE_REPORTS,
            PermissionEnum.MANAGE_TENANTS
          ],
          type: "Product Performance Report"
        },
        {
          path: MallOrdersListPath,
          permissions: [
            PermissionEnum.MANAGE_REPORTS,
            PermissionEnum.MANAGE_TENANTS
          ],
          type: "Mall Orders Report"
        },
        {
          path: OrderLinesListPath,
          permissions: [
            PermissionEnum.MANAGE_REPORTS,
            PermissionEnum.MANAGE_STORE_ORDERS
          ],
          type: "Order Lines"
        },
        {
          path: TransportReportListPath,
          permissions: [
            PermissionEnum.MANAGE_REPORTS,
            PermissionEnum.MANAGE_STORE_ORDERS
          ],
          type: "Transport Report"
        }
      ],
      title: "BI"
    }
  ];

  return (
    <Container>
      <PageHeader
        title={intl.formatMessage({
          defaultMessage: "Reports",
          description: "Reports page"
        })}
      >
        {/* <Button
          color="primary"
          variant="contained"
          onClick={() => openModal("generate")}
        >
          <FormattedMessage
            defaultMessage="Generate Report"
            description="button"
          />
        </Button> */}
      </PageHeader>
      <Grid variant="repeated">
        {reportCategories?.map((category, categoryIndex) => (
          <RequirePermissions
            userPermissions={user.userPermissions}
            requiredPermissions={category?.permissions}
          >
            <Card key={categoryIndex}>
              <CardContent className={classes.cardContent}>
                <SVG src={category.icon} />
                <div>
                  <Typography className={classes.cardTitle} color="primary">
                    {category.title}
                  </Typography>
                  <Typography className={classes.cardDescription}>
                    {category.description}
                  </Typography>
                  {category.reportTypes.map((report, reportIndex) => (
                    <RequirePermissions
                      userPermissions={user.userPermissions}
                      requiredPermissions={report?.permissions}
                    >
                      <Button
                        className={classes.cardButton}
                        key={reportIndex}
                        size="small"
                        color="primary"
                        onClick={() => navigate(report.path)}
                      >
                        {report.type}
                      </Button>
                    </RequirePermissions>
                  ))}
                </div>
              </CardContent>
            </Card>
          </RequirePermissions>
        ))}
      </Grid>
      <GenerateReportDialog
        open={params?.action === "generate"}
        onClose={closeModal}
        onSubmit={null}
      ></GenerateReportDialog>
    </Container>
  );
};

export default ReportsPage;
